import { memo, useEffect, useRef, useState } from 'react';
import { Focusable } from 'react-aria-components';

import { merge } from '@/components';
import { Flex, Text, Tooltip, TooltipTrigger } from '@/components/core';
import { DocumentIcon, ImageIcon, PDFIcon, SaveIcon } from '@/components/icons';
import { useAgentRunTree } from '@/hooks';
import { DivProps } from '@/utils';

export type AgentRunDocumentCardProps = DivProps & {
  agentId: string;
  runId: string;
};

const IAgentRunDocumentCard = ({ agentId, runId, className, ...rest }: AgentRunDocumentCardProps) => {
  const { taskDocument } = useAgentRunTree({ agentId, runId });
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTextTruncated, setIsTextTruncated] = useState(false);

  useEffect(() => {
    const textElement = textRef.current;
    if (!textElement) return;

    const checkIfTruncated = () => {
      if (!textElement) return;
      setIsTextTruncated(textElement.scrollWidth > textElement.clientWidth);
    };

    checkIfTruncated();

    const resizeObserver = new ResizeObserver(checkIfTruncated);
    resizeObserver.observe(textElement);

    return () => {
      if (textElement) resizeObserver.unobserve(textElement);
      resizeObserver.disconnect();
    };
  }, [taskDocument]);

  if (!taskDocument) {
    return (
      <Flex className={merge('items-center', className)} {...rest}>
        <Flex className="gap-sm items-center justify-center p-2">
          <DocumentIcon className="absolute size-[1.6875rem]" />
          <SaveIcon className="!size-[0.625rem] translate-y-[4px]" />
        </Flex>
        <div className="ml-[9px] h-3 w-48 animate-pulse rounded-lg bg-gray-200" />
      </Flex>
    );
  }

  return (
    <Flex className={merge('gap-sm items-center', className)} {...rest}>
      {taskDocument.contentType === 'application/pdf' ? (
        <PDFIcon className="min-h-[1.6875rem] min-w-[1.6875rem]" />
      ) : (
        <ImageIcon className="min-h-[1.6875rem] min-w-[1.6875rem]" />
      )}
      <TooltipTrigger isDisabled={!isTextTruncated}>
        <Tooltip variant="primary" placement="right">
          <Text slot="description">{taskDocument.name}</Text>
        </Tooltip>
        <Focusable>
          <Text role="button" ref={textRef} className="truncate text-gray-800">
            {taskDocument.name}
          </Text>
        </Focusable>
      </TooltipTrigger>
    </Flex>
  );
};

export const AgentRunDocumentCard = memo(IAgentRunDocumentCard) as typeof IAgentRunDocumentCard;
